<template>
  <div class="app-view">
    <!-- Top bar -->
    <header class="app-view__header">
      <button class="back-btn" @click="goBack">← Dashboard</button>
      <div class="header__title">
        <span class="header__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
        </span>
        <div>
          <span class="header__name">Literaturförderung – Förderantrag</span>
          <span class="header__sub">Antrag auf Förderung deutschsprachiger Literatur · Berliner Senatsverwaltung</span>
        </div>
      </div>
      <div class="header__badge">FormIQ 1 · Chat Assist</div>
    </header>

    <!-- Showcase banner -->
    <ShowcaseBanner />

    <!-- Main layout: form + sidebar -->
    <div class="app-view__body">

      <!-- Left: Form area -->
      <div class="form-area">
        <component
          :is="currentStepComponent"
          v-model="formData"
          :showErrors="showErrors"
        />
      </div>

      <!-- Right: Sidebar navigation -->
      <aside class="sidebar">
        <div class="sidebar__header">
          <span class="sidebar__icon">📋</span>
          <span class="sidebar__title">Formularnavigation</span>
        </div>
        <nav class="sidebar__nav">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="sidebar__item"
            :class="{
              'sidebar__item--active': index === currentStep,
              'sidebar__item--done': index < currentStep,
              'sidebar__item--locked': index > currentStep,
            }"
            @click="goToStep(index)"
          >
            <div class="sidebar__indicator">
              <span v-if="index < currentStep" class="indicator--done">✓</span>
              <span v-else-if="index === currentStep" class="indicator--active">{{ index + 1 }}</span>
              <span v-else class="indicator--pending">{{ index + 1 }}</span>
            </div>
            <span class="sidebar__label">{{ step }}</span>
          </div>
        </nav>
        <div class="sidebar__progress">
          <div class="progress-bar">
            <div class="progress-bar__fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <span class="progress-text">{{ currentStep + 1 }} von {{ steps.length }} Schritten</span>
        </div>
      </aside>
    </div>

    <!-- Navigation bar at bottom -->
    <div class="nav-bar">
      <button
        class="nav-btn nav-btn--secondary"
        :disabled="currentStep === 0"
        @click="prevStep"
      >
        ← Zurück
      </button>
      <div class="nav-bar__center">
        <span class="nav-bar__step-label">Schritt {{ currentStep + 1 }}: {{ steps[currentStep] }}</span>
      </div>
      <button
        v-if="currentStep < steps.length - 1"
        class="nav-btn nav-btn--primary"
        @click="nextStep"
      >
        Weiter →
      </button>
      <button
        v-else
        class="nav-btn nav-btn--finish"
        @click="showFinish = true"
      >
        Antrag absenden ✓
      </button>
    </div>

    <!-- Selection popover -->
    <SelectionPopover />

    <!-- Floating chat -->
    <ChatWindow form-id="form_1" :client-id="resolvedClientId" :ui-payload="currentUiPayload" />

    <!-- Finish modal -->
    <div v-if="showFinish" class="modal-overlay" @click.self="showFinish = false">
      <div class="modal">
<!--        <div class="modal__icon">🎉</div>-->
        <h2>Antrag eingereicht!</h2>
        <p>Dies ist eine Showcase-Demo – es wurden keine echten Daten übermittelt. Vielen Dank für das Testen von FormIQ Chat Assist.</p>
        <div class="modal__actions">
          <button class="nav-btn nav-btn--secondary" @click="showFinish = false">Schließen</button>
          <button class="nav-btn nav-btn--primary" @click="goBack">← Zurück zum Dashboard</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import ShowcaseBanner from '../components/ShowcaseBanner.vue';
import ChatWindow from '../components/ChatWindow.vue';
import SelectionPopover from '../components/SelectionPopover.vue';
import LitStep1 from '../forms/literature/LitStep1.vue';
import LitStep2 from '../forms/literature/LitStep2.vue';
import LitStep3 from '../forms/literature/LitStep3.vue';
import LitStep4 from '../forms/literature/LitStep4.vue';
import LitStep5 from '../forms/literature/LitStep5.vue';
import { flowAssignmentApi } from '../services/flowAssignmentApi.js';

const FLOW_KEY = 'formiq1';
const DEFAULT_CLIENT_ID = 'form1_foerderantrag';

export default {
  name: 'ApplicationViewA',
  components: { ShowcaseBanner, ChatWindow, SelectionPopover, LitStep1, LitStep2, LitStep3, LitStep4, LitStep5 },

  setup() {
    const router = useRouter();
    const showFinish = ref(false);
    const showErrors = ref(false);
    const resolvedClientId = ref(DEFAULT_CLIENT_ID);

    onMounted(async () => {
      try {
        const assignment = await flowAssignmentApi.getFlowAssignment(FLOW_KEY);
        if (assignment?.client_id) resolvedClientId.value = assignment.client_id;
      } catch {
        // keep the default client_id if the lookup fails
      }
    });

    const steps = [
      'Informationen',
      'Datenschutz',
      'Online-Ausweis',
      'Persönliche Daten',
      'Zusammenfassung',
    ];

    const stepComponents = [LitStep1, LitStep2, LitStep3, LitStep4, LitStep5];

    const currentStep = ref(0);
    const currentStepComponent = computed(() => stepComponents[currentStep.value]);
    const progressPercent = computed(() => Math.round((currentStep.value / (steps.length - 1)) * 100));

    // Form data store for all steps
    const formData = ref({
      // Step 1
      bestaetigung: false,
      // Step 3
      id_verwendung: null,
      // Step 4 - personal
      anrede: '',
      titel: '',
      kuenstlername: '',
      vorname: '',
      nachname: '',
      geschlecht: '',
      geburtsdatum: '',
      geburtsort: '',
      staatsangehoerigkeit: '',
      email: '',
      telefon: '',
      mobil: '',
      strasse: '',
      hausnummer: '',
      plz: '',
      ort: '',
    });

    const validateCurrentStep = () => {
      const d = formData.value;
      switch (currentStep.value) {
        case 0: return d.bestaetigung === true;
        case 1: return true; // Info-only step
        case 2: return d.id_verwendung !== null;
        case 3:
          return d.anrede && d.vorname && d.nachname && d.geschlecht &&
                 d.geburtsdatum && d.geburtsort && d.staatsangehoerigkeit &&
                 d.email && d.telefon && d.strasse && d.hausnummer && d.plz && d.ort;
        default: return true;
      }
    };

    const goToStep = (index) => {
      if (index <= currentStep.value) {
        currentStep.value = index;
        showErrors.value = false;
      }
    };

    const nextStep = () => {
      if (!validateCurrentStep()) {
        showErrors.value = true;
        return;
      }
      showErrors.value = false;
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    const prevStep = () => {
      showErrors.value = false;
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    const goBack = () => router.push('/dashboard');

    // Screen IDs matching the step index
    const screenIds = [
      'lit-informationen',
      'lit-datenschutz',
      'lit-eid',
      'lit-personaldaten',
      'lit-zusammenfassung',
    ];

    // Field keys visible on each step (for ui_payload filtering)
    const stepFields = [
      ['bestaetigung'],
      [],
      ['id_verwendung'],
      ['anrede','titel','kuenstlername','vorname','nachname','geschlecht','geburtsdatum',
       'geburtsort','staatsangehoerigkeit','email','telefon','mobil','strasse','hausnummer','plz','ort'],
      [],
    ];

    // Required fields per step (mirrors validateCurrentStep)
    const stepRequired = {
      0: ['bestaetigung'],
      2: ['id_verwendung'],
      3: ['anrede','vorname','nachname','geschlecht','geburtsdatum','geburtsort',
          'staatsangehoerigkeit','email','telefon','strasse','hausnummer','plz','ort'],
    };

    const currentUiPayload = computed(() => {
      const step  = currentStep.value;
      const d     = formData.value;
      const sid   = screenIds[step];
      const fields = stepFields[step] || [];
      const required = stepRequired[step] || [];

      const field_values = {};
      fields.forEach(k => { field_values[k] = d[k]; });

      const field_errors = {};
      if (showErrors.value) {
        required.forEach(k => {
          const v = d[k];
          if (v === '' || v === null || v === false || v === undefined) {
            field_errors[k] = 'Dieses Feld ist erforderlich.';
          }
        });
      }

      const form_progress = { current_step: step + 1, total_steps: steps.length, steps: {} };
      steps.forEach((name, i) => {
        const status = i < step ? 'completed' : i === step ? 'in_progress' : 'pending';
        form_progress.steps[screenIds[i]] = { name, status };
      });

      return { screen_id: sid, field_values, field_errors, form_progress };
    });

    return {
      steps, currentStep, currentStepComponent, progressPercent,
      formData, showErrors,
      showFinish, resolvedClientId, goToStep, nextStep, prevStep, goBack,
      currentUiPayload,
    };
  },
};
</script>

<style scoped>
.app-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #eef0f4;
  overflow: hidden;
}

/* Header */
.app-view__header {
  background: #ffffff;
  border-bottom: 3px solid #c0121c;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #0f172a;
  flex-shrink: 0;
  gap: 16px;
}

.back-btn {
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #1e293b;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.back-btn:hover { background: #e2e8f0; }

.header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}
.header__icon { font-size: 22px; display: flex; align-items: center; color: #c0121c; }
.header__name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
}
.header__sub {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.header__badge {
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #475569;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

/* Body: form + sidebar */
.app-view__body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
}

/* Form area */
.form-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: #fff;
  border-right: 1px solid #d0d7de;
}

/* Sidebar */
.sidebar {
  width: 240px;
  flex-shrink: 0;
  background: #f5f7fa;
  border-left: 1px solid #d0d7de;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar__header {
  padding: 16px 16px 12px;
  border-bottom: 2px solid #c0121c;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar__icon { font-size: 16px; }

.sidebar__title {
  font-size: 12px;
  font-weight: 700;
  color: #c0121c;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sidebar__nav {
  flex: 1;
  padding: 8px 0;
}

.sidebar__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: default;
  border-left: 3px solid transparent;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}

.sidebar__item--done {
  cursor: pointer;
  border-left-color: #1a7a45;
}
.sidebar__item--done:hover {
  background: #e8f5ee;
}

.sidebar__item--active {
  background: #eef4fb;
  border-left-color: #003366;
}

.sidebar__item--locked {
  opacity: 0.55;
}

.sidebar__indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.indicator--done {
  background: #1a7a45;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.indicator--active {
  background: #003366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.18);
}

.indicator--pending {
  background: #e2e8f0;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.sidebar__label {
  font-size: 12px;
  color: #1a1a1a;
  font-weight: 500;
  line-height: 1.35;
}

.sidebar__item--active .sidebar__label {
  font-weight: 700;
  color: #003366;
}

.sidebar__item--done .sidebar__label {
  color: #155534;
}

.sidebar__progress {
  padding: 14px 16px;
  border-top: 1px solid #d0d7de;
}

.progress-bar {
  height: 5px;
  background: #d0d7de;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar__fill {
  height: 100%;
  background: #003366;
  border-radius: 3px;
  transition: width 0.35s ease;
}

.progress-text {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

/* Navigation bar at bottom */
.nav-bar {
  background: #f5f7fa;
  border-top: 2px solid #d0d7de;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
}

.nav-bar__center {
  flex: 1;
  text-align: center;
}

.nav-bar__step-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.nav-btn {
  padding: 10px 28px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s, background 0.15s;
  letter-spacing: 0.01em;
}
.nav-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.nav-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.nav-btn--primary { background: #003366; color: #fff; }
.nav-btn--primary:hover:not(:disabled) { background: #002244; opacity: 1; }
.nav-btn--secondary {
  background: #ffffff;
  color: #1a1a1a;
  border: 1px solid #adb5bd;
}
.nav-btn--secondary:hover:not(:disabled) { background: #f0f4f9; opacity: 1; }
.nav-btn--finish { background: #1a7a45; color: #fff; }
.nav-btn--finish:hover:not(:disabled) { background: #155534; opacity: 1; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal {
  background: #fff;
  border-radius: 8px;
  padding: 40px 36px;
  max-width: 440px;
  width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  border-top: 4px solid #c0121c;
}
.modal__icon { font-size: 52px; }
.modal h2 { font-size: 22px; font-weight: 800; color: #1a1a2e; }
.modal p { font-size: 14px; color: #555; line-height: 1.6; }
.modal__actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* Responsive: hide sidebar on small screens */
@media (max-width: 700px) {
  .sidebar { display: none; }
}
</style>
